import { lazy } from "react";
import { createBrowserRouter, defer } from "react-router-dom";

const DocPage = lazy(() => import("@components/Pages/Doc"));

const MainPages = {
    layout: lazy(() => import("@components/Layouts/MainLayout")),
    index: lazy(() => import("@components/Pages/LandingPage")),
    error404: lazy(() => import("@components/Pages/Error404Page")),
    errorPermission: lazy(
        () => import("@components/Pages/PermissionErrorPage"),
    ),
    about: lazy(() => import("@components/Pages/AboutPage")),
    auth: {
        layout: lazy(() => import("@components/Layouts/auth/AuthLayout")),
        logout: lazy(() => import("@components/Pages/auth/Logout")),
        login: lazy(() => import("@components/Pages/auth/Login")),
        register: lazy(() => import("@components/Pages/auth/Register")),
        confirmEmail: lazy(() => import("@components/Pages/auth/ConfirmEmail")),
        forgotPassword: lazy(
            () => import("@components/Pages/auth/ForgottenPassword"),
        ),
        refreshToken: lazy(() => import("@components/Routes/RefreshToken")),
    },
    whitepaper: lazy(() => import("@components/Pages/Whitepaper")),
    audiencePage: lazy(
        () => import("@components/Pages/audience/GenericAudiencePage"),
    ),
    entityPage: lazy(() => import("@components/Pages/SharedEntityPage")),
};

const AppRoutes = createBrowserRouter([
    // Define our routes in here!
    {
        path: "/",
        element: <MainPages.layout />,
        children: [
            {
                path: "*", // Catch all
                element: <MainPages.error404 />,
            },
            {
                path: "not-found", // Just in case we change the catch all
                element: <MainPages.error404 />,
            },
            {
                path: "permission-required",
                element: <MainPages.errorPermission />,
            },
            {
                index: true,
                element: <MainPages.index />,
            },
            {
                path: "/whitepaper",
                element: <MainPages.whitepaper />,
            },
            {
                path: "/audience",
                children: [
                    {
                        path: "material-manufacturers",
                        loader: () => {
                            return defer({
                                data: new Promise((res) => {
                                    import(
                                        "@utils/constants/pageData/materialAudience"
                                    ).then((data) =>
                                        res(data.MaterialAudienceData),
                                    );
                                }),
                            });
                        },
                        element: <MainPages.audiencePage />,
                    },
                    {
                        path: "compliance-schemes",
                        loader: () => {
                            return defer({
                                data: new Promise((res) => {
                                    import(
                                        "@utils/constants/pageData/complianceSchemes"
                                    ).then((data) =>
                                        res(data.CoplianceSchemesData),
                                    );
                                }),
                            });
                        },
                        element: <MainPages.audiencePage />,
                    },
                    {
                        path: "technology-companies",
                        loader: () => {
                            return defer({
                                data: new Promise((res) => {
                                    import(
                                        "@utils/constants/pageData/technologyCompanies"
                                    ).then((data) =>
                                        res(data.TechnologyCompaniesData),
                                    );
                                }),
                            });
                        },
                        element: <MainPages.audiencePage />,
                    },
                    {
                        path: "product-manufacturers",
                        loader: () => {
                            return defer({
                                data: new Promise((res) => {
                                    import(
                                        "@utils/constants/pageData/productManufacturers"
                                    ).then((data) =>
                                        res(data.ProductManufacturersData),
                                    );
                                }),
                            });
                        },
                        element: <MainPages.audiencePage />,
                    },
                    {
                        path: "packaging-manufacturers",
                        loader: () => {
                            return defer({
                                data: new Promise((res) => {
                                    import(
                                        "@utils/constants/pageData/packagingManufacturers"
                                    ).then((data) =>
                                        res(data.PackagingManufacturersData),
                                    );
                                }),
                            });
                        },
                        element: <MainPages.audiencePage />,
                    },
                    {
                        path: "retailers",
                        loader: () => {
                            return defer({
                                data: new Promise((res) => {
                                    import(
                                        "@utils/constants/pageData/retailersAudience"
                                    ).then((data) => res(data.RetailersData));
                                }),
                            });
                        },
                        element: <MainPages.audiencePage />,
                    },
                    {
                        path: "brands",
                        loader: () => {
                            return defer({
                                data: new Promise((res) => {
                                    import(
                                        "@utils/constants/pageData/brandsAudience"
                                    ).then((data) => res(data.BrandsData));
                                }),
                            });
                        },
                        element: <MainPages.audiencePage />,
                    },
                    {
                        index: true,
                        path: "*",
                        element: <MainPages.error404 />,
                    },
                ],
            },
        ],
    },
    {
        path: "/docs/api",
        element: <DocPage />,
    },
]);

export default AppRoutes;
